import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link as MuiLink,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import clsx from 'clsx';
import { Link, Route } from 'react-router-dom';
import { LayoutPageGrid } from 'ui/LayoutGrid/LayoutGrid';
import { usePermissionPage } from 'hooks';
import { useMeContext } from 'components';
import {
  ClientApplication,
  ClientVersionSummariesQuery,
  VersionPropsFragment,
  useClientVersionSummariesQuery,
  useClientVersionsQuery,
  useCheckClientVersionQuery,
} from 'schema/serverTypes';
import { CreateVersionPage } from '../CreateVersionPage';
import {
  formatVersion,
  getUpdateZones,
  parseVersion,
  platformLabel,
  UpdateRequirement,
  VersionNumber,
} from '../versionUtils';
import { useStyles } from './useStyles';

const platforms = [ClientApplication.Android, ClientApplication.Ios];

const THRESHOLD_HINT = 'Порог — максимальная версия с отметкой обязательности.';

const VersionList = ({ versions }: { versions: VersionPropsFragment[] }) => {
  const classes = useStyles();
  return (
    <ul className={classes.historyList}>
      {versions.map((version) => (
        <li key={version.id} className={classes.historyItem}>
          <span className={classes.historyVersion}>{formatVersion(version)}</span>
          {version.mandatory && <span className={classes.historyMark}>обязательная</span>}
        </li>
      ))}
    </ul>
  );
};

const CheckResult = ({
  client,
  version,
}: {
  client: ClientApplication;
  version: VersionNumber;
}) => {
  const { data, loading, error, refetch } = useCheckClientVersionQuery({
    variables: { client, ...version },
    // Mandatory здесь вычислен для установленной версии и не должен менять флаг записи в cache.
    fetchPolicy: 'no-cache',
  });
  if (loading)
    return (
      <Box role="status" my={1}>
        <CircularProgress size={20} /> Проверяем версию…
      </Box>
    );
  if (error)
    return (
      <Alert severity="error">
        Не удалось проверить версию.{' '}
        <Button
          onClick={() => {
            void refetch();
          }}
        >
          Повторить
        </Button>
      </Alert>
    );
  if (!data) return null;
  const result = data.latestClientVersion;
  return (
    <Alert severity={!result ? 'success' : result.mandatory ? 'warning' : 'info'}>
      {!result
        ? 'Обновление не требуется: более новая версия не зарегистрирована.'
        : `${result.mandatory ? 'Обязательное' : 'Необязательное'} обновление до ${formatVersion(
            result
          )}.`}
    </Alert>
  );
};

const VersionChecker = ({ onClose }: { onClose: () => void }) => {
  const classes = useStyles();
  const [client, setClient] = useState(ClientApplication.Android);
  const [input, setInput] = useState('');
  const [submitted, setSubmitted] = useState<{ value: string; attempt: number } | null>(null);
  const parsed = parseVersion(input);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-labelledby="version-check-title">
      <DialogTitle id="version-check-title">Проверка версии</DialogTitle>
      <DialogContent dividers>
        <form
          id="version-check-form"
          className={classes.checkerForm}
          onSubmit={(event) => {
            event.preventDefault();
            if (parsed) setSubmitted({ value: input, attempt: (submitted?.attempt || 0) + 1 });
          }}
        >
          <TextField
            select
            className={classes.checkerPlatform}
            id="check-version-client"
            label="Платформа"
            variant="outlined"
            value={client}
            onChange={(event) => {
              setClient(event.target.value as ClientApplication);
              setSubmitted(null);
            }}
          >
            {platforms.map((platform) => (
              <MenuItem key={platform} value={platform}>
                {platformLabel(platform)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            className={classes.checkerField}
            id="installed-version"
            label="Установленная версия"
            variant="outlined"
            placeholder="1.2.3"
            value={input}
            onChange={(event) => {
              setInput(event.target.value);
              setSubmitted(null);
            }}
            error={input.length > 0 && !parsed}
            helperText="Три целых неотрицательных числа: major.minor.patch"
            inputProps={{ maxLength: 32 }}
          />
          {submitted?.value === input && parsed && (
            <div className={classes.checkerResult}>
              <CheckResult key={submitted.attempt} client={client} version={parsed} />
            </div>
          )}
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
        <Button
          form="version-check-form"
          type="submit"
          variant="contained"
          color="primary"
          disabled={!parsed}
        >
          Проверить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const requirementLabels: Record<UpdateRequirement, string> = {
  mandatory: 'обязательно',
  optional: 'необязательно',
  none: 'не требуется',
};

const UpdateRuleScale = ({
  latest,
  mandatory,
}: {
  latest: VersionNumber;
  mandatory?: VersionNumber | null;
}) => {
  const classes = useStyles();
  const zoneClasses: Record<UpdateRequirement, string> = {
    mandatory: classes.zoneMandatory,
    optional: classes.zoneOptional,
    none: classes.zoneNone,
  };
  return (
    <ul className={classes.zones}>
      {getUpdateZones(latest, mandatory).map(({ requirement, range }) => (
        <li key={requirement} className={clsx(classes.zone, zoneClasses[requirement])}>
          <span className={classes.zoneRange}>{range}</span>
          <span className={classes.zoneRequirement}>{requirementLabels[requirement]}</span>
        </li>
      ))}
    </ul>
  );
};

const PlatformRow = ({
  client,
  summary,
  summaryMissing,
  onRetry,
  onOpenHistory,
}: {
  client: ClientApplication;
  summary?: ClientVersionSummariesQuery['clientVersionSummaries'][number];
  summaryMissing: boolean;
  onRetry: () => void;
  onOpenHistory: () => void;
}) => {
  const classes = useStyles();
  const latest = summary?.latestVersion;
  const previous = summary?.previousVersions || [];
  const headingId = `version-platform-${client}`;
  return (
    <section className={classes.platform} aria-labelledby={headingId}>
      <div className={classes.row}>
        <Typography id={headingId} className={classes.nameCell} variant="h4" component="h2">
          {platformLabel(client)}
        </Typography>
        <div className={classes.versionCell}>
          <span className={classes.cellLabel}>Последняя версия</span>
          <Typography className={classes.versionValue} variant="h3" component="p">
            {latest ? formatVersion(latest) : '—'}
          </Typography>
        </div>
        <div className={classes.ruleCell}>
          <span className={classes.cellLabel}>Правило обновления</span>
          {summaryMissing ? (
            <Alert severity="error">
              Сервер не вернул данные платформы. <Button onClick={onRetry}>Повторить</Button>
            </Alert>
          ) : latest ? (
            <UpdateRuleScale latest={latest} mandatory={summary?.mandatoryVersion} />
          ) : (
            summary && (
              <Typography className={classes.emptyRule}>
                Версий пока нет. Правило обновления появится после регистрации первой версии.
              </Typography>
            )
          )}
        </div>
        <div className={classes.addCell}>
          <Button
            component={Link}
            to={`/versions/add?client=${client}`}
            variant="contained"
            color="primary"
          >
            Добавить версию
          </Button>
        </div>
        {latest && (
          <div className={classes.previousCell}>
            {previous.length > 0 &&
              `Ранее: ${previous
                .map(
                  (version) =>
                    `${formatVersion(version)}${version.mandatory ? ' (обязательная)' : ''}`
                )
                .join(', ')}, `}
            <MuiLink
              className={classes.historyLink}
              component="button"
              type="button"
              onClick={onOpenHistory}
            >
              {`${previous.length > 0 ? 'вся' : 'Вся'} история (${summary?.totalCount})`}
            </MuiLink>
          </div>
        )}
      </div>
    </section>
  );
};

const PlatformHistory = ({
  client,
  onClose,
}: {
  client: ClientApplication;
  onClose: () => void;
}) => {
  const classes = useStyles();
  const [moreError, setMoreError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const { data, loading, error, refetch, fetchMore } = useClientVersionsQuery({
    variables: { client, first: 20 },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const connection = data?.clientVersions;
  const loadMore = async () => {
    if (!connection?.pageInfo.endCursor || loadingMore) return;
    setLoadingMore(true);
    setMoreError(false);
    try {
      await fetchMore({
        variables: { client, first: 20, after: connection.pageInfo.endCursor },
        updateQuery: (previous, { fetchMoreResult }) => {
          if (!fetchMoreResult?.clientVersions) return previous;
          return {
            ...fetchMoreResult,
            clientVersions: {
              ...fetchMoreResult.clientVersions,
              nodes: [
                ...(previous.clientVersions?.nodes || []),
                ...(fetchMoreResult.clientVersions.nodes || []),
              ],
            },
          };
        },
      });
    } catch {
      setMoreError(true);
    } finally {
      setLoadingMore(false);
    }
  };
  const versions = (connection?.nodes || []).filter((item): item is VersionPropsFragment => !!item);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs" aria-labelledby="version-history-title">
      <DialogTitle disableTypography>
        <Typography id="version-history-title" variant="h4" component="h2">
          История версий {platformLabel(client)}
        </Typography>
        {connection && !error && (
          <Typography className={classes.historyCount}>
            Всего записей: {connection.totalCount}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers className={classes.historyContent}>
        {loading && (
          <div role="status" className={clsx(classes.historyState, classes.historyLoading)}>
            <CircularProgress size={20} /> Загрузка истории…
          </div>
        )}
        {error ? (
          <div className={classes.historyState}>
            <Alert severity="error">
              Не удалось загрузить историю.{' '}
              <Button
                onClick={() => {
                  void refetch();
                }}
              >
                Повторить
              </Button>
            </Alert>
          </div>
        ) : (
          connection && (
            <>
              {versions.length ? (
                <VersionList versions={versions} />
              ) : (
                <Typography className={classes.historyState}>
                  Версии ещё не зарегистрированы.
                </Typography>
              )}
              {moreError && (
                <div className={classes.historyState}>
                  <Alert severity="error">
                    Не удалось загрузить следующую страницу. Уже загруженные записи сохранены.
                  </Alert>
                </div>
              )}
              {connection.pageInfo.hasNextPage && (
                <div className={classes.historyMore}>
                  <Button
                    disabled={loadingMore}
                    onClick={() => {
                      void loadMore();
                    }}
                  >
                    {loadingMore ? 'Загрузка…' : moreError ? 'Повторить загрузку' : 'Показать ещё'}
                  </Button>
                </div>
              )}
            </>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
};

export const VersionsPage = () => {
  const { user } = useMeContext();
  usePermissionPage('/wards', user?.permissions.clientVersions.manage);
  const { data, loading, error, refetch } = useClientVersionSummariesQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
  const [historyClient, setHistoryClient] = useState<ClientApplication | null>(null);
  const [checkerOpen, setCheckerOpen] = useState(false);
  const classes = useStyles();
  const retry = () => {
    void refetch();
  };
  return (
    <LayoutPageGrid title="Версии клиентов">
      <Box width="100%" minWidth={0}>
        {loading && (
          <Box role="status" mb={2}>
            <CircularProgress size={24} /> Загрузка версий…
          </Box>
        )}
        {error && (
          <Box mb={2}>
            <Alert severity="error">
              Не удалось загрузить версии. <Button onClick={retry}>Повторить</Button>
            </Alert>
          </Box>
        )}
        <Card className={classes.sheet}>
          <div className={classes.header}>
            <span>Платформа</span>
            <span>Последняя версия</span>
            <span className={classes.headerRule}>
              Правило обновления
              <Tooltip title={THRESHOLD_HINT}>
                <IconButton size="small" aria-label="Как определяется порог">
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </span>
          </div>
          {platforms.map((client) => (
            <PlatformRow
              key={client}
              client={client}
              summary={
                !error && !loading
                  ? data?.clientVersionSummaries.find((item) => item.client === client)
                  : undefined
              }
              summaryMissing={
                !error &&
                !loading &&
                !data?.clientVersionSummaries.some((item) => item.client === client)
              }
              onRetry={retry}
              onOpenHistory={() => setHistoryClient(client)}
            />
          ))}
          <div className={classes.ruleNote}>{THRESHOLD_HINT}</div>
        </Card>
        <Typography className={classes.description} color="textSecondary">
          Зарегистрированные версии и правила обновления. Эти данные не подтверждают публикацию в
          магазинах и не показывают версии на устройствах пользователей.
        </Typography>
        <Box mt={2}>
          <Button variant="outlined" onClick={() => setCheckerOpen(true)}>
            Проверить версию
          </Button>
        </Box>
        {checkerOpen && <VersionChecker onClose={() => setCheckerOpen(false)} />}
        {historyClient && (
          <PlatformHistory
            key={historyClient}
            client={historyClient}
            onClose={() => setHistoryClient(null)}
          />
        )}
        <Route exact path="/versions/add" component={CreateVersionPage} />
      </Box>
    </LayoutPageGrid>
  );
};
